import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { bookingService } from "./booking.services";
import { GetBookingParams } from "./booking.types";
import { BOOKING_KEYS } from "./keys";

export const useGetBookings = (params?: GetBookingParams) => {
  return useQuery({
    queryKey: BOOKING_KEYS.GET_BOOKINGS(params),
    queryFn: () => bookingService.getBookings(params),
    staleTime: 2 * 60 * 1000, // 2 minutes for booking lists
    gcTime: 15 * 60 * 1000, // 15 minutes cache time
  });
};

export const useGetBooking = (id?: string | null) => {
  return useQuery({
    queryKey: BOOKING_KEYS.GET_BOOKING(id!),
    queryFn: () => bookingService.getBooking(id!),
    enabled: !!id,
    staleTime: 3 * 60 * 1000, // 3 minutes for individual bookings
    gcTime: 20 * 60 * 1000, // 20 minutes cache time
  });
};

export const useGetUpcomingBookings = (enabled: boolean = true) => {
  return useQuery({
    queryKey: BOOKING_KEYS.GET_UPCOMING_BOOKINGS(),
    queryFn: () => bookingService.getUpcomingBookings(),
    enabled,
  });
};

export const useGetPastBookings = (enabled: boolean = true) => {
  return useQuery({
    queryKey: BOOKING_KEYS.GET_PAST_BOOKINGS(),
    queryFn: () => bookingService.getPastBookings(),
    enabled,
  });
};

export const useGetMyBookings = (params?: GetBookingParams) => {
  return useQuery({
    queryKey: BOOKING_KEYS.GET_MY_BOOKINGS(params),
    queryFn: () => bookingService.getMyBookings(params),
  });
};

export const useGetClientBookings = (clientId: string, params?: GetBookingParams) => {
  return useQuery({
    queryKey: [...BOOKING_KEYS.GET_MY_BOOKINGS(params), clientId],
    queryFn: () => bookingService.getClientBookings(clientId, params),
    enabled: !!clientId,
  });
};

export const useGetMyBookingsInfinite = (params?: GetBookingParams) => {
  return useInfiniteQuery({
    queryKey: BOOKING_KEYS.GET_MY_BOOKINGS(params),
    queryFn: ({ pageParam = 1 }) =>
      bookingService.getMyBookings({ ...params, page: pageParam }),
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.currentPage < lastPage.meta.totalPages) {
        return lastPage.meta.currentPage + 1;
      }
      return undefined;
    },
    initialPageParam: params?.page ?? 1,
  });
};

export const useGetMyServiceClientBookings = (params?: GetBookingParams) => {
  return useQuery({
    queryKey: BOOKING_KEYS.GET_MY_SERVICE_CLIENT_BOOKINGS(params),
    queryFn: () => bookingService.getMyServiceClientBookings(params),
  });
};

export const useGetAllMyRelatedBookings = (params?: GetBookingParams) => {
  return useQuery({
    queryKey: BOOKING_KEYS.GET_ALL_MY_RELATED_BOOKINGS(params),
    queryFn: () => bookingService.getAllMyRelatedBookings(params),
  });
};

export const useGetUpcomingBookingsIncludingServices = (
  params?: GetBookingParams
) => {
  return useQuery({
    queryKey: BOOKING_KEYS.GET_UPCOMING_BOOKINGS_INCLUDING_SERVICES(params),
    queryFn: () => bookingService.getUpcomingBookingsIncludingServices(params),
  });
};

export const useGetPastBookingsIncludingServices = (
  params?: GetBookingParams
) => {
  return useQuery({
    queryKey: BOOKING_KEYS.GET_PAST_BOOKINGS_INCLUDING_SERVICES(params),
    queryFn: () => bookingService.getPastBookingsIncludingServices(params),
  });
};

export const useGetJobTicketsByServiceTickets = (
  params?: GetBookingParams,
  enabled = false
) => {
  return useQuery({
    queryKey: BOOKING_KEYS.GET_JOB_TICKETS_BY_SERVICE_TICKETS(params),
    queryFn: () => bookingService.getJobTicketsByServiceTickets(params),
    enabled,
  });
};
